import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { merge } from "lodash";
import { isFetchResult, reusableFetch } from "@/utils/useFetch";
import {
  IInvoiceSlice,
  IInvoiceState,
  IGetAllInvoicesResponse,
  IGetServiceUsagesResponse,
  IGetInvoiceByIdResponse,
  IInvoiceFormState,
  IInvoiceExpandedSections,
  IInvoiceFilterDate,
} from "@/store/slices/types/invoiceSliceTypes";
import { INVOICE_STATUS } from "@/constants";

export const getAllInvoices = createAsyncThunk<IGetAllInvoicesResponse, unknown>(
  "invoice/getAllInvoices",
  async (data, { rejectWithValue }) => {
    const response = await reusableFetch(`tenant-invoice/all`, "POST", data, rejectWithValue);
    if (!isFetchResult(response)) {
      return rejectWithValue(response);
    }
    return response.data as IGetAllInvoicesResponse;
  },
);

export const getInvoiceById = createAsyncThunk<IGetInvoiceByIdResponse, string>(
  "invoice/getInvoiceById",
  async (invoiceId, { rejectWithValue }) => {
    const response = await reusableFetch(`tenant-invoice/${invoiceId}`, "GET", null, rejectWithValue);
    if (!isFetchResult(response)) {
      return rejectWithValue(response);
    }
    return response.data as IGetInvoiceByIdResponse;
  },
);

export const getTenantServiceUsages = createAsyncThunk<
  IGetServiceUsagesResponse,
  { tenancyBillingConfigId: string; month: number; year: number }
>("invoice/getTenantServiceUsages", async (data, { rejectWithValue }) => {
  const response = await reusableFetch(`service-usage/get-tenant-service-usage`, "POST", data, rejectWithValue);
  if (!isFetchResult(response)) {
    return rejectWithValue(response);
  }
  return response.data as IGetServiceUsagesResponse;
});

export const saveInvoiceDraft = createAsyncThunk<unknown, unknown>(
  "invoice/saveDraft",
  async (data, { rejectWithValue }) => {
    const response = await reusableFetch(`tenant-invoice/save-draft-invoice`, "POST", data, rejectWithValue);
    return response;
  },
);

export const sendInvoice = createAsyncThunk<unknown, unknown>("invoice/send", async (data, { rejectWithValue }) => {
  const response = await reusableFetch(`tenant-invoice/send`, "POST", data, rejectWithValue);
  return response;
});

export const updateInvoiceDraft = createAsyncThunk<unknown, { invoiceId: string; payload: unknown }>(
  "invoice/updateDraft",
  async ({ invoiceId, payload }, { rejectWithValue }) => {
    const response = await reusableFetch(
      `tenant-invoice/update-draft-invoice/${invoiceId}`,
      "PUT",
      payload,
      rejectWithValue,
    );
    return response;
  },
);

export const updateInvoiceStatus = createAsyncThunk<unknown, { invoiceId: string; status: string }>(
  "invoice/updateStatus",
  async ({ invoiceId, status }, { rejectWithValue }) => {
    const response = await reusableFetch(`tenant-invoice/status/${invoiceId}`, "PATCH", { status }, rejectWithValue);
    return response;
  },
);

const todayIso = () => {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
};

const previousMonth = () => {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { month: prev.getMonth() + 1, year: prev.getFullYear() };
};

const buildInitialForm = (): IInvoiceFormState => {
  const { month, year } = previousMonth();
  return {
    invoiceTitle: "",
    tenancyBillingConfigId: null,
    issuedDate: todayIso(),
    billingMonth: month,
    billingYear: year,
    recipient: { firstName: "", lastName: "", email: "", billingName: "", address: "", billingAddress: "" },
    ccEmails: [],
    feeItems: [],
    status: INVOICE_STATUS.draft,
    notes: "",
    isAutoChargeEnabled: false,
    isStripeInvoice: false,
  };
};

const initialExpandedSections: IInvoiceExpandedSections = {
  invoiceInfo: true,
  recipient: false,
  feesItems: false,
  serviceUsages: false,
};

const initialState: IInvoiceState = {
  gettingInvoices: false,
  page: 1,
  invoices: {
    list: [],
  },
  filterDate: {
    startDate: null,
    endDate: null,
  },
  tenantDetails: null,
  gettingTenantDetails: false,
  tenantServiceUsages: null,
  gettingServiceUsages: false,
  savingInvoice: false,
  sendingInvoice: false,
  form: buildInitialForm(),
  showErrors: false,
  expandedSections: { ...initialExpandedSections },
  currentInvoice: null,
  gettingInvoiceById: false,
  updatingInvoice: false,
};

export const invoiceSlice = createSlice({
  name: "invoice",
  initialState,
  reducers: {
    saveValue: (state, action) => merge(state, action.payload),
    setInvoiceFilterDate: (state, action: PayloadAction<IInvoiceFilterDate>) => {
      state.filterDate = action.payload;
    },
    resetInvoiceState: () => initialState,
    resetTenantInvoiceDetails: (state) => {
      state.tenantDetails = null;
      state.tenantServiceUsages = null;
    },
    setInvoiceForm: (state, action: PayloadAction<Partial<IInvoiceFormState>>) => {
      state.form = { ...state.form, ...action.payload };
    },
    setInvoiceExpandedSection: (
      state,
      action: PayloadAction<{ key: keyof IInvoiceExpandedSections; value: boolean }>,
    ) => {
      state.expandedSections[action.payload.key] = action.payload.value;
    },
    setInvoiceShowErrors: (state, action: PayloadAction<boolean>) => {
      state.showErrors = action.payload;
    },
    resetInvoiceForm: (state) => {
      state.form = buildInitialForm();
      state.showErrors = false;
      state.expandedSections = { ...initialExpandedSections };
      state.currentInvoice = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllInvoices.fulfilled, (state, { payload }: PayloadAction<IGetAllInvoicesResponse>) => {
        state.invoices = payload?.data || ({} as IInvoiceState["invoices"]);
        state.gettingInvoices = false;
      })
      .addCase(getAllInvoices.pending, (state) => {
        state.gettingInvoices = true;
      })
      .addCase(getAllInvoices.rejected, (state) => {
        state.gettingInvoices = false;
      })
      .addCase(getTenantServiceUsages.fulfilled, (state, { payload }) => {
        state.tenantServiceUsages = payload?.data || null;
        state.gettingServiceUsages = false;
      })
      .addCase(getTenantServiceUsages.pending, (state) => {
        state.gettingServiceUsages = true;
      })
      .addCase(getTenantServiceUsages.rejected, (state) => {
        state.gettingServiceUsages = false;
      })
      .addCase(saveInvoiceDraft.pending, (state) => {
        state.savingInvoice = true;
      })
      .addCase(saveInvoiceDraft.fulfilled, (state) => {
        state.savingInvoice = false;
      })
      .addCase(saveInvoiceDraft.rejected, (state) => {
        state.savingInvoice = false;
      })
      .addCase(sendInvoice.pending, (state) => {
        state.sendingInvoice = true;
      })
      .addCase(sendInvoice.fulfilled, (state) => {
        state.sendingInvoice = false;
      })
      .addCase(sendInvoice.rejected, (state) => {
        state.sendingInvoice = false;
      })
      .addCase(getInvoiceById.pending, (state) => {
        state.gettingInvoiceById = true;
      })
      .addCase(getInvoiceById.fulfilled, (state, { payload }: PayloadAction<IGetInvoiceByIdResponse>) => {
        const invoice = payload?.data || null;
        state.currentInvoice = invoice;
        state.gettingInvoiceById = false;
        if (!invoice) return;
        const feeItems = (invoice.fees || []).map((li, idx) => ({
          ...li,
          id: `invoice-${idx}-${Date.now()}`,
        }));
        state.form = {
          invoiceTitle: invoice.title || "",
          tenancyBillingConfigId: invoice.tenancyBillingConfig?._id || null,
          issuedDate: invoice.issuedDate || todayIso(),
          billingMonth: invoice.month || state.form.billingMonth,
          billingYear: invoice.year || state.form.billingYear,
          recipient: {
            firstName: invoice.recipient?.firstName || invoice.tenancyBillingConfig?.firstName || "",
            lastName: invoice.recipient?.lastName || invoice.tenancyBillingConfig?.lastName || "",
            email: invoice.recipient?.email || invoice.tenancyBillingConfig?.email || "",
            billingName: invoice.recipient?.billingName || invoice.tenancyBillingConfig?.billingName || "",
            address: invoice.recipient?.address || invoice.tenancyBillingConfig?.address || "",
            billingAddress: invoice.recipient?.billingAddress || invoice.tenancyBillingConfig?.billingAddress || "",
          },
          ccEmails: invoice?.tenancyBillingConfig?.ccEmails || [],
          feeItems,
          status: invoice.status || state.form.status,
          notes: invoice.notes || "",
          isAutoChargeEnabled: invoice.isAutoChargeEnabled ?? false,
          isStripeInvoice: Boolean(invoice.stripe_invoice_id),
        };
        state.tenantServiceUsages = {
          services: invoice.services || [],
          serviceUsageIds: invoice.service_usage_ids || [],
          summary: invoice.summary,
          feesItems: invoice.fees || [],
          gstLabel: invoice.gstLabel || "",
          month: invoice.month || state.form.billingMonth,
          year: invoice.year || state.form.billingYear,
          isAutoChargeEnabled: invoice.isAutoChargeEnabled ?? false,
        };
        state.expandedSections = { invoiceInfo: true, recipient: true, feesItems: true, serviceUsages: true };
      })
      .addCase(getInvoiceById.rejected, (state) => {
        state.gettingInvoiceById = false;
      })
      .addCase(updateInvoiceDraft.pending, (state) => {
        state.updatingInvoice = true;
        state.savingInvoice = true;
      })
      .addCase(updateInvoiceDraft.fulfilled, (state) => {
        state.updatingInvoice = false;
        state.savingInvoice = false;
      })
      .addCase(updateInvoiceDraft.rejected, (state) => {
        state.updatingInvoice = false;
        state.savingInvoice = false;
      });
  },
});

export const {
  saveValue,
  setInvoiceFilterDate,
  resetInvoiceState,
  resetTenantInvoiceDetails,
  setInvoiceForm,
  setInvoiceExpandedSection,
  setInvoiceShowErrors,
  resetInvoiceForm,
} = invoiceSlice.actions;

export const invoiceStateSelector = (state: IInvoiceSlice) => state.invoice;
export const invoiceSelector = (state: IInvoiceSlice) => state.invoice.invoices;
